import { create } from 'zustand'
import type { Message, Conversation } from '../types'
import { api, makeStreamingRequest, mcpApi } from '../services/api'

// MCP Tool Call types
export interface MCPToolCall {
  id: string
  toolName: string
  params: Record<string, any>
  status: 'pending' | 'executing' | 'success' | 'error'
  result?: any
  error?: string
}

// Helper function to parse MCP tool calls from content
function parseMCPToolCalls(content: string): Array<{ toolName: string; params: Record<string, any> }> {
  console.log('[parseMCPToolCalls] Checking content for MCP tool calls...')
  console.log('[parseMCPToolCalls] Content length:', content.length)
  console.log('[parseMCPToolCalls] Content preview:', content.substring(0, 500))
  
  // Check if content contains MCP tags
  const hasMcpTag = content.includes('<mcp>')
  const hasExecuteTool = content.includes('<execute-tool>')
  console.log('[parseMCPToolCalls] Has <mcp>:', hasMcpTag, 'Has <execute-tool>:', hasExecuteTool)
  
  const toolCalls: Array<{ toolName: string; params: Record<string, any> }> = []
  
  // Pattern 1: <mcp> <tool-call>...</tool-call> </mcp> (original format)
  const toolCallRegex = /<mcp>\s*<tool-call>\s*<tool-name>(.*?)<\/tool-name>\s*<tool-params>(.*?)<\/tool-params>\s*<\/tool-call>\s*<\/mcp>/g
  
  // Pattern 2: <mcp> mcp.tool.name({...}) </mcp> (AI simplified format like mcp.fs.ls)
  const simpleRegex = /<mcp>\s*(mcp\.[\w.]+)\(([^)]*)\)\s*<\/mcp>/g
  
  // Pattern 3: <mcp> <tool.name> content </tool.name> </mcp> (custom MCP format like <tool_code>...</tool_code>)
  const customRegex = /<mcp>\s*<(\w+)>(.*?)<\/\1>\s*<\/mcp>/gs
  
  // Pattern 4: <mcp> tool.name content </mcp> (inline format)
  const inlineRegex = /<mcp>\s*([\w.]+\.[\w.]+)\s+(.*?)\s*<\/mcp>/g
  
  // Pattern 5: <mcp> <execute-tool> <tool-name> name </tool-name> <parameters>...</parameters> </execute-tool> </mcp> (Gemini format)
  const geminiRegex = /<mcp>\s*<execute-tool>\s*<tool-name>\s*([^<]+)\s*<\/tool-name>\s*<parameters>\s*(.*?)\s*<\/parameters>\s*<\/execute-tool>\s*<\/mcp>/gis
  
  // Try Pattern 1 first
  let match
  while ((match = toolCallRegex.exec(content)) !== null) {
    try {
      const toolName = match[1].trim()
      const paramsStr = match[2].trim()
      let params = {}
      try {
        params = JSON.parse(paramsStr)
      } catch {
        params = { value: paramsStr }
      }
      toolCalls.push({ toolName, params })
    } catch (e) {
      console.warn('[ChatStore] Failed to parse MCP tool call:', match[0])
    }
  }
  
  // Try Pattern 2
  while ((match = simpleRegex.exec(content)) !== null) {
    try {
      const toolName = match[1].trim()
      const argsStr = match[2].trim()
      let params = {}
      if (argsStr) {
        try {
          params = JSON.parse(argsStr)
        } catch {
          const namedArgs: Record<string, any> = {}
          const argPairs = argsStr.split(',').forEach((pair) => {
            const [key, value] = pair.split(':').map((s) => s.trim())
            if (key && value) {
              try {
                namedArgs[key] = JSON.parse(value)
              } catch {
                namedArgs[key] = value.replace(/["']/g, '')
              }
            }
          })
          params = namedArgs
        }
      }
      toolCalls.push({ toolName, params })
    } catch (e) {
      console.warn('[ChatStore] Failed to parse simplified MCP tool call:', match[0])
    }
  }
  
  // Try Pattern 3 (custom MCP format like <tool_code>fs.list_files(path=".")</tool_code>)
  content.replace(customRegex, (fullMatch, tagName, innerContent) => {
    try {
      const toolName = tagName.trim()
      const rawContent = innerContent.trim()
      
      // Try to parse as key=value format first (e.g., path=".")
      const params: Record<string, any> = {}
      
      // Check for key=value patterns
      const valueMatches = rawContent.match(/(\w+)=["']([^"']*)["']/g)
      if (valueMatches) {
        valueMatches.forEach((vm: string) => {
          const [key, ...rest] = vm.split('=')
          const value = rest.join('=')
          try {
            params[key.trim()] = JSON.parse(value.trim().replace(/["']/g, ''))
          } catch {
            params[key.trim()] = value.trim().replace(/["']/g, '')
          }
        })
      }
      
      // If no key=value found, check for function call format like fs.list_files(path=".")
      if (Object.keys(params).length === 0) {
        const funcMatch = rawContent.match(/(\w+\.\w+)\(([^)]*)\)/)
        if (funcMatch) {
          // Extract function name and arguments
          const funcName = funcMatch[1]
          const funcArgs = funcMatch[2]
          
          // Parse arguments
          const argParams: Record<string, any> = {}
          const argMatches = funcArgs.matchAll(/(\w+)=["']([^"']*)["']/g)
          for (const am of argMatches) {
            try {
              argParams[am[1]] = JSON.parse(am[2])
            } catch {
              argParams[am[1]] = am[2]
            }
          }
          
          params.command = funcName
          if (Object.keys(argParams).length > 0) {
            params.arguments = argParams
          }
        } else {
          params.command = rawContent
        }
      }
      
      toolCalls.push({ toolName: `mcp.${toolName}`, params })
    } catch (e) {
      console.warn('[ChatStore] Failed to parse custom MCP tool call:', fullMatch)
    }
    return fullMatch
  })
  
  // Try Pattern 4 (inline format)
  while ((match = inlineRegex.exec(content)) !== null) {
    try {
      const toolName = match[1].trim()
      const rest = match[2].trim()
      
      const pathMatch = rest.match(/path=["'](.+?)["']/)
      let params: Record<string, any> = {}
      
      if (pathMatch) {
        params.path = pathMatch[1]
        params.content = rest.replace(/path=["'].*?["']/, '').trim()
      } else {
        params.content = rest
      }
      
      toolCalls.push({ toolName, params })
    } catch (e) {
      console.warn('[ChatStore] Failed to parse inline MCP tool call:', match[0])
    }
  }
  
  // Try Pattern 5 (Gemini format: <execute-tool><tool-name>...</tool-name><parameters>...</parameters></execute-tool>)
  try {
    // More robust regex that handles newlines and nested tags
    const geminiMatches = content.matchAll(/<mcp>\s*<execute-tool>\s*<tool-name>\s*([^<]+)\s*<\/tool-name>\s*<parameters>\s*([\s\S]*?)\s*<\/parameters>\s*<\/execute-tool>\s*<\/mcp>/gi)
    for (const gm of geminiMatches) {
      try {
        const toolName = gm[1].trim()
        const paramsContent = gm[2].trim()
        
        // Parse parameters - extract nested key=value pairs
        const params: Record<string, any> = {}
        const paramMatches = paramsContent.matchAll(/<(\w+)>\s*([\s\S]*?)\s*<\/\1>/g)
        for (const pm of paramMatches) {
          const key = pm[1]
          const value = pm[2].trim()
          try {
            params[key] = JSON.parse(value)
          } catch {
            params[key] = value
          }
        }
        
        toolCalls.push({ toolName: `mcp.${toolName}`, params })
        console.log('[ChatStore] Parsed MCP tool call:', { toolName, params })
      } catch (e) {
        console.warn('[ChatStore] Failed to parse Gemini MCP tool call:', gm[0])
      }
    }
  } catch (e) {
    // regex error handling
  }
  
  return toolCalls
}

interface ChatState {
  // Conversations
  conversations: Conversation[]
  currentConversation: Conversation | null
  isLoadingConversations: boolean
  
  // Messages
  messages: Message[]
  isStreaming: boolean
  streamingContent: string
  streamingError: string | null
  
  // UI State
  chatMode: 'assistant' | 'agentic' | 'multi-agent'
  selectedProvider: string
  selectedModel: string
  isSidebarOpen: boolean
  searchQuery: string
  
  // Context Mode (NEW: Standard | Git | MCP)
  contextMode: 'standard' | 'git' | 'mcp'
  selectedGitRepo: string | null
  selectedMcpServer: string | null
  selectedCodebaseId: string | null
  availableCodebases: Array<{ id: string; name: string; rootPath: string; indexStatus: string }>
  availableTools: string[]
  
  // MCP Tool Calls
  mcpToolCalls: MCPToolCall[]
  isExecutingMCPTool: boolean
  
  // Actions
  fetchConversations: (userId: string) => Promise<void>
  createConversation: (userId: string, title: string, mode: 'assistant' | 'agentic' | 'multi-agent') => Promise<Conversation>
  selectConversation: (conversation: Conversation) => void
  deleteConversation: (id: string) => Promise<void>
  sendMessage: (userId: string, content: string) => Promise<void>
  cancelStreaming: () => void
  retryLastMessage: () => Promise<void>
  
  setChatMode: (mode: 'assistant' | 'agentic' | 'multi-agent') => void
  setSelectedProvider: (provider: string) => void
  setSelectedModel: (model: string) => void
  toggleSidebar: () => void
  setSearchQuery: (query: string) => void
  clearCurrentConversation: () => void
  setContextMode: (mode: 'standard' | 'git' | 'mcp') => void
  setSelectedGitRepo: (repo: string | null) => void
  setSelectedMcpServer: (server: string | null) => void
  setSelectedCodebaseId: (codebaseId: string | null) => void
  fetchAvailableCodebases: () => Promise<void>
  setAvailableTools: (tools: string[]) => void
  executeMCPTool: (serverId: string, toolName: string, params: Record<string, any>) => Promise<boolean>
}

export const useChatStore = create<ChatState>((set, get) => ({
  // Initial State
  conversations: [],
  currentConversation: null,
  isLoadingConversations: false,
  messages: [],
  isStreaming: false,
  streamingContent: '',
  streamingError: null,
  chatMode: 'assistant',
  selectedProvider: 'claude',
  selectedModel: 'claude-sonnet-4-20250514',
  isSidebarOpen: true,
  searchQuery: '',
  
  // Context Mode defaults
  contextMode: 'standard',
  selectedGitRepo: null,
  selectedMcpServer: null,
  selectedCodebaseId: null,
  availableCodebases: [],
  availableTools: [],
  
  // MCP Tool Calls
  mcpToolCalls: [],
  isExecutingMCPTool: false,

  // Fetch Conversations
  fetchConversations: async (userId: string) => {
    set({ isLoadingConversations: true })
    try {
      const response = await api.get<{ data: Conversation[] }>('/chat', { params: { userId } })
      if (response.data && response.data.data) {
        set({ conversations: response.data.data })
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error)
    } finally {
      set({ isLoadingConversations: false })
    }
  },

  // Create Conversation
  createConversation: async (userId: string, title: string, mode: 'assistant' | 'agentic' | 'multi-agent') => {
    const response = await api.post<{ data: Conversation }>('/chat', { userId, title, mode })
    if (response.data && response.data.data) {
      set((state) => ({
        conversations: [response.data!.data, ...state.conversations],
        currentConversation: response.data!.data,
        messages: [],
        streamingContent: '',
        streamingError: null
      }))
      return response.data!.data
    }
    throw new Error('Failed to create conversation')
  },

  // Select Conversation
  selectConversation: (conversation: Conversation) => {
    set({
      currentConversation: conversation,
      messages: conversation.messages || [],
      streamingContent: '',
      streamingError: null
    })
  },

  // Delete Conversation
  deleteConversation: async (id: string) => {
    try {
      await api.delete(`/chat/${id}`)
      set((state) => ({
        conversations: state.conversations.filter((c: Conversation) => c.id !== id),
        currentConversation: state.currentConversation?.id === id ? null : state.currentConversation,
        messages: state.currentConversation?.id === id ? [] : state.messages
      }))
    } catch (error) {
      console.error('Failed to delete conversation:', error)
      throw error
    }
  },

  // Send Message
  sendMessage: async (userId: string, content: string) => {
    console.log('[ChatStore] sendMessage called:', { userId, content })
    const { currentConversation, messages, chatMode, selectedProvider, selectedModel, contextMode, selectedGitRepo, selectedMcpServer, selectedCodebaseId } = get()
    console.log('[ChatStore] State:', { hasConversation: !!currentConversation, chatMode, selectedProvider, selectedModel, contextMode, selectedCodebaseId })
    console.log('[ChatStore] Selected MCP Server:', selectedMcpServer)
    
    // Debug: Log the actual values being sent
    console.log('[ChatStore] DEBUG - contextMode value:', JSON.stringify(contextMode), 'type:', typeof contextMode)
    console.log('[ChatStore] DEBUG - Is contextMode === "mcp"?:', contextMode === 'mcp')
    
    // Optimistic update - add user message immediately
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date().toISOString()
    }
    
    // Clear previous tool calls
    set({ mcpToolCalls: [] })
    
    set({
      messages: [...messages, userMessage],
      isStreaming: true,
      streamingContent: '',
      streamingError: null
    })

    try {
      console.log('[ChatStore] Calling makeStreamingRequest...')
      let fullResponse = ''
      let pendingToolCalls: Array<{ toolName: string; params: Record<string, any> }> = []
      
      // Track which tool calls we've already executed to avoid duplicates
      const executedToolCalls = new Set<string>()
      
      // Call API with streaming (include context mode)
      const generator = makeStreamingRequest(
        {
          message: content,
          conversationId: currentConversation?.id,
          provider: selectedProvider,
          mode: chatMode,
          model: selectedModel,
          contextMode,
          gitRepo: selectedGitRepo,
          mcpServer: selectedMcpServer,
          codebaseId: selectedCodebaseId
        },
        (chunk: string) => {
          fullResponse += chunk
          set((state) => ({
            streamingContent: state.streamingContent + chunk
          }))
          
          // Only parse MCP tool calls if we have complete tags (contains both <mcp> and </mcp>)
          // This avoids parsing on every character of streaming content
          if (fullResponse.includes('<mcp>') && fullResponse.includes('</mcp>')) {
            // Parse tool calls from the streaming content
            const parsedToolCalls = parseMCPToolCalls(fullResponse)
            
            // Debug: Log if we found any tool calls
            if (parsedToolCalls.length > 0) {
              console.log('[ChatStore] Found tool calls:', parsedToolCalls)
            }
            
            // Check for new tool calls that haven't been executed yet
            for (const toolCall of parsedToolCalls) {
              const toolKey = `${toolCall.toolName}-${JSON.stringify(toolCall.params)}`
              console.log('[ChatStore] Checking tool call:', { toolKey, contextMode, hasServer: !!selectedMcpServer })
              if (!executedToolCalls.has(toolKey) && contextMode === 'mcp' && selectedMcpServer) {
                executedToolCalls.add(toolKey)
                pendingToolCalls.push(toolCall)
                
                // Execute the tool call immediately
                const toolCallId = Date.now().toString() + Math.random().toString(36).slice(2)
                set((state) => ({
                  mcpToolCalls: [
                    ...state.mcpToolCalls,
                    {
                      id: toolCallId,
                      toolName: toolCall.toolName,
                      params: toolCall.params,
                      status: 'executing'
                    }
                  ]
                }))
                
                // Extract the actual tool name for API call (remove mcp. prefix if present)
                const apiToolName = toolCall.toolName.replace(/^mcp\./, '')
                
                // Execute and update
                mcpApi.callTool(selectedMcpServer, apiToolName, toolCall.params)
                  .then((response) => {
                    set((state) => ({
                      mcpToolCalls: state.mcpToolCalls.map((tc) =>
                        tc.id === toolCallId
                          ? { ...tc, status: 'success', result: response.data }
                          : tc
                      )
                    }))
                  })
                  .catch((error) => {
                    set((state) => ({
                      mcpToolCalls: state.mcpToolCalls.map((tc) =>
                        tc.id === toolCallId
                          ? { ...tc, status: 'error', error: error.message || 'Unknown error' }
                          : tc
                      )
                    }))
                  })
              }
            }
          }
        },
        (metadata) => {
          // Store available tools from metadata
          if (metadata.availableTools) {
            set({ availableTools: metadata.availableTools })
          }
        }
      )

      for await (const chunk of generator) {
        fullResponse += chunk
      }

      console.log('[ChatStore] makeStreamingRequest completed')
      
      // After streaming completes, add the AI message
      const aiMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: get().streamingContent || fullResponse || 'No response',
        timestamp: new Date().toISOString(),
        provider: selectedProvider,
        model: selectedModel
      }

      set((state) => ({
        messages: [...state.messages, aiMessage],
        isStreaming: false,
        streamingContent: '',
        streamingError: null
      }))

    } catch (error) {
      console.error('[ChatStore] Streaming error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      
      set((state) => ({
        isStreaming: false,
        streamingError: errorMessage,
        // Add error message
        messages: [...state.messages, {
          id: Date.now().toString(),
          role: 'assistant',
          content: `Sorry, something went wrong: ${errorMessage}. Please try again.`,
          timestamp: new Date().toISOString(),
          provider: selectedProvider
        } as Message]
      }))
    }
  },

  // Cancel Streaming
  cancelStreaming: () => {
    set({ isStreaming: false, streamingContent: '', streamingError: null })
  },

  // Retry Last Message
  retryLastMessage: async () => {
    const { messages } = get()
    const lastUserMessage = [...messages].reverse().find(m => m.role === 'user')
    if (lastUserMessage) {
      set((state) => ({
        messages: state.messages.filter(m => m.role !== 'assistant' || m.id === 'streaming')
      }))
      await get().sendMessage(get().currentConversation?.userId || '', lastUserMessage.content)
    }
  },

  // UI Actions
  setChatMode: (mode: 'assistant' | 'agentic' | 'multi-agent') => {
    set({ chatMode: mode })
  },

  setSelectedProvider: (provider: string) => {
    set({ selectedProvider: provider })
  },

  setSelectedModel: (model: string) => {
    set({ selectedModel: model })
  },

  toggleSidebar: () => {
    set((state) => ({ isSidebarOpen: !state.isSidebarOpen }))
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query })
  },

  clearCurrentConversation: () => {
    set({
      currentConversation: null,
      messages: [],
      streamingContent: '',
      streamingError: null
    })
  },

  // Context Mode Actions
  setContextMode: (mode: 'standard' | 'git' | 'mcp') => {
    set({ contextMode: mode })
  },

  setSelectedGitRepo: (repo: string | null) => {
    set({ selectedGitRepo: repo })
  },

  setSelectedMcpServer: (server: string | null) => {
    set({ selectedMcpServer: server })
  },
  
  setSelectedCodebaseId: (codebaseId: string | null) => {
    set({ selectedCodebaseId: codebaseId })
  },
  
  fetchAvailableCodebases: async () => {
    try {
      const response = await api.get<{ data: Array<{ id: string; name: string; rootPath: string; indexStatus: string }> }>('/codebases')
      if (response.data && response.data.data) {
        set({ availableCodebases: response.data.data })
      }
    } catch (error) {
      console.error('[ChatStore] Failed to fetch codebases:', error)
      set({ availableCodebases: [] })
    }
  },
  
  setAvailableTools: (tools: string[]) => {
    set({ availableTools: tools })
  },
  
  // Execute MCP Tool
  executeMCPTool: async (serverId: string, toolName: string, params: Record<string, any>) => {
    const toolCallId = Date.now().toString()
    
    // Add pending tool call
    set((state) => ({
      mcpToolCalls: [
        ...state.mcpToolCalls,
        {
          id: toolCallId,
          toolName,
          params,
          status: 'executing'
        }
      ],
      isExecutingMCPTool: true
    }))
    
    try {
      console.log('[ChatStore] Executing MCP tool:', { serverId, toolName, params })
      const response = await mcpApi.callTool(serverId, toolName, params)
      
      // Update tool call with success
      set((state) => ({
        mcpToolCalls: state.mcpToolCalls.map((tc) =>
          tc.id === toolCallId
            ? { ...tc, status: 'success', result: response.data }
            : tc
        ),
        isExecutingMCPTool: false
      }))
      
      return true
    } catch (error: any) {
      console.error('[ChatStore] MCP tool execution failed:', error)
      
      // Update tool call with error
      set((state) => ({
        mcpToolCalls: state.mcpToolCalls.map((tc) =>
          tc.id === toolCallId
            ? { ...tc, status: 'error', error: error.message || 'Unknown error' }
            : tc
        ),
        isExecutingMCPTool: false
      }))
      
      return false
    }
  }
}))
